<template>
  <div class="min-h-screen bg-gray-100 flex items-center justify-center">
    <div class="max-w-md w-full bg-white rounded-lg shadow-md p-8">
      <div class="text-center mb-8">
        <div class="flex flex-col items-center">
          <img :src="logoUrl" alt="AUAS Logo" class="h-16 w-16 mb-3" />
          <h1 class="text-2xl font-bold text-gray-900">AUAS Staff Portal Form</h1>
        </div>
        <p class="text-gray-600 mt-2">
          {{ directLogin ? 'Enter your email and PIN to login' : 'Enter your AUAS email to receive a login PIN' }}
        </p>
      </div>



      <form @submit.prevent="handleSubmit" class="space-y-6">
        <div>
          <label for="email" class="block text-sm font-medium text-gray-700">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            v-model="email"
            :disabled="submittedSuccess || loading || (pinSent && !directLogin)"
            placeholder="your.name@auas.edu.krd"
            class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            required
          />
        </div>

        <div v-if="pinSent || directLogin">
          <label for="pin" class="block text-sm font-medium text-gray-700">
            Enter PIN
          </label>
          <input
            id="pin"
            type="text"
            v-model="pin"
            :disabled="submittedSuccess || loading"
            :placeholder="directLogin ? 'Enter your existing PIN' : 'Enter the PIN sent to your email'"
            class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            required
          />
        </div>

        <!-- Toggle for direct login -->
        <div v-if="!pinSent && !directLogin" class="text-center">
          <button
            type="button"
            @click="switchToDirectLogin"
            class="text-sm text-blue-600 hover:text-blue-500 underline"
          >
            Already have a PIN? Login directly
          </button>
        </div>

        <div v-if="error" class="text-red-600 text-sm">
          {{ error }}
        </div>

        <div v-if="success" class="text-green-600 text-sm">
          {{ success }}
        </div>

        <button
          type="submit"
          :disabled="submittedSuccess || loading"
          class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span v-if="loading" class="flex items-center">
            <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </span>
          <span v-else>
            {{ directLogin ? 'Login' : (pinSent ? 'Verify PIN' : 'Send PIN') }}
          </span>
        </button>

        <div v-if="!submittedSuccess && (pinSent || directLogin)" class="text-center space-y-2">
          <button
            type="button"
            @click="resetForm"
            class="text-sm text-blue-600 hover:text-blue-500"
          >
            Use different email
          </button>
          <div v-if="!directLogin">
            <button
              type="button"
              @click="switchToDirectLogin"
              class="text-sm text-gray-600 hover:text-gray-500"
            >
              Already have a PIN? Login directly
            </button>
          </div>
          <div v-else>
            <button
              type="button"
              @click="switchToRequestPin"
              class="text-sm text-gray-600 hover:text-gray-500"
            >
              Need a new PIN? Request one
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuth } from '../stores/auth';

const router = useRouter();
const route = useRoute();
const auth = useAuth();
const logoUrl = '/AUAS.png';

const email = ref('');
const pin = ref('');
const loading = ref(false);
const pinSent = ref(false);
const directLogin = ref(false);
const error = ref('');
const success = ref('');
const submittedSuccess = ref(false);
const countdown = ref(3);

const handleSubmit = async () => {
  error.value = '';
  success.value = '';
  loading.value = true;

  try {
    if (directLogin.value) {
      // Direct login with email and PIN
      await auth.directLogin(email.value, pin.value);
      router.push('/dashboard');
    } else if (!pinSent.value) {
      // Send PIN
      await auth.requestPin(email.value);
      pinSent.value = true;
      success.value = 'PIN sent to your email. Please check your inbox.';
    } else {
      // Verify PIN
      await auth.verifyPin(pin.value);
      router.push('/dashboard');
    }
  } catch (err: any) {
    const status = err?.response?.status;
    error.value = err?.response?.data?.error || 'An error occurred. Please try again.';
  } finally {
    loading.value = false;
  }
};

const resetForm = () => {
  email.value = '';
  pin.value = '';
  pinSent.value = false;
  directLogin.value = false;
  error.value = '';
  success.value = '';
};

const switchToDirectLogin = () => {
  directLogin.value = true;
  pinSent.value = false;
  pin.value = '';
  error.value = '';
  success.value = '';
};

const switchToRequestPin = () => {
  directLogin.value = false;
  pinSent.value = false;
  pin.value = '';
  error.value = '';
  success.value = '';
};

onMounted(() => {
  // Logic identifying 'submitted' status has been removed to allow re-login.
  // Existing query params are ignored so user can log in again.
});
</script>
